import React, { useCallback, useContext, useEffect } from "react";
// import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import {
  PageChangeEvent,
  Worker,
  Plugin,
  RenderPage,
  RenderPageProps,
} from "@react-pdf-viewer/core";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "./PDFViewer.css";
import { HighlightArea } from "@react-pdf-viewer/highlight";
import { IconButton, Paper, Stack, Typography, useTheme } from "@mui/material";
import { ChatContext } from "../../Chat/ChatContainer";
import { toolbarPlugin, ToolbarSlot } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

type HighlightsType = {
  id: number;
  image: string;
  selectionRegion: HighlightArea;
}[];

interface PDFViewerProps {
  pdfFile: string;
  onPageChangeCallback?: (e: PageChangeEvent) => void;
  highlightSelectionPlugin?: Plugin;
  highlightPlugin?: any;
  previousHighlights?: Array<any>;
  highlights?: HighlightsType;
  customRenderPage?: RenderPage;
  studentMode?: boolean;
  isExpanded?: boolean;
}

export const PDFViewerForStudents: React.FC<PDFViewerProps> = ({
  pdfFile,
  onPageChangeCallback,
  highlightSelectionPlugin,
  highlightPlugin,
  previousHighlights,
  highlights,
  customRenderPage,
  studentMode,
  isExpanded,
}) => {
  const theme = useTheme();

  const viewerTheme = theme.palette.mode === "dark" ? "dark" : "light";

  const {
    pageNavigationPluginInstance,
    currentPDFPage,
    setCurrentPDFPage,
    multipleCourses,
  } = useContext(ChatContext);

  // get the current page number for the PDF viewer from the context
  useEffect(() => {
    if (setCurrentPDFPage) {
      setCurrentPDFPage(currentPDFPage ?? 1);
    }
    console.log(`Current page: ${currentPDFPage} from PDFViewer.tsx`);
  }, [currentPDFPage, setCurrentPDFPage]);

  // mark current page
  const markCurrentPageRenderer: RenderPage = useCallback(
    (props: RenderPageProps) => (
      <>
        {props.canvasLayer.children}
        <div
          style={{
            alignItems: "center",
            display: "flex",
            height: "100%",
            justifyContent: "center",
            left: 0,
            position: "absolute",
            top: 0,
            width: "100%",
            border:
              props.pageIndex === currentPDFPage ? "2px solid red" : "none",
          }}
        ></div>
        {props.annotationLayer.children}
        {props.textLayer.children}
      </>
    ),
    [currentPDFPage]
  );

  // PDF viewer for student mode
  if (studentMode) {
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    // const { ZoomPopover } = zoomPluginInstance;

    // console.log(pdfFile);
    return (
      <div>
        <div>
          <Worker workerUrl="/assets/pdf.worker.min.js">
            {pdfFile ? (
              <div>
                <div
                  className="myPDFViewer"
                  style={
                    multipleCourses
                      ? isExpanded
                        ? { height: "calc(100vh - 188px)" }
                        : { height: "calc(100vh - 158px)" }
                      : { height: "calc(100vh - 120px)" }
                  }
                >
                  <Toolbar>
                    {(props: ToolbarSlot) => {
                      const { ZoomIn, Zoom, ZoomOut } = props;
                      return (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          }}
                        >
                          <ZoomOut>
                            {(props) => (
                              <IconButton onClick={props.onClick}>
                                <ZoomOutIcon />
                              </IconButton>
                            )}
                          </ZoomOut>
                          <Paper
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                            }}
                          >
                            <Zoom />
                          </Paper>
                          <ZoomIn>
                            {(props) => (
                              <IconButton onClick={props.onClick}>
                                <ZoomInIcon />
                              </IconButton>
                            )}
                          </ZoomIn>
                          <Typography>
                            Looking at slide <b>{(currentPDFPage ?? 0) + 1}</b>
                          </Typography>
                        </Stack>
                      );
                    }}
                  </Toolbar>
                  <Viewer
                    theme={viewerTheme}
                    fileUrl={pdfFile}
                    onPageChange={onPageChangeCallback}
                    plugins={[
                      pageNavigationPluginInstance ?? {},
                      toolbarPluginInstance,
                    ]}
                    renderPage={markCurrentPageRenderer}
                  />
                </div>
              </div>
            ) : (
              <p>Loading your class lectures...</p>
            )}
          </Worker>
        </div>
      </div>
    );
  } else {
    return null; // Return null if no pdfFile is provided
  }
};
